import React, { useState } from 'react';
import { Phone, Bot, Shield, TrendingUp, Zap, CheckCircle, ArrowRight, Play, Mic, Brain, Cpu, Users, Star, Menu, X, Sparkles, Activity } from 'lucide-react';

const BlueCollarTechWebsite = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('professional');
  const [missedCalls, setMissedCalls] = useState(10);
  const [jobValue, setJobValue] = useState(1000);
  const [activeIndustry, setActiveIndustry] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const industries = [
    { id: 'hvac', name: 'HVAC', icon: '🔥' },
    { id: 'plumbing', name: 'Plumbing', icon: '🔧' },
    { id: 'electrical', name: 'Electrical', icon: '⚡' },
    { id: 'roofing', name: 'Roofing', icon: '🏠' },
    { id: 'landscaping', name: 'Landscaping', icon: '🌿' },
    { id: 'construction', name: 'Construction', icon: '🏗️' },
    { id: 'painting', name: 'Painting', icon: '🎨' },
    { id: 'cleaning', name: 'Cleaning', icon: '🧹' }
  ];

  const calculateROI = () => {
    const monthlyMissedRevenue = missedCalls * 4 * jobValue;
    const investment = selectedPlan === 'starter' ? 99 : selectedPlan === 'professional' ? 299 : 499;
    const roi = ((monthlyMissedRevenue - investment) / investment * 100).toFixed(0);
    return { revenue: monthlyMissedRevenue, roi };
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // Send to Lambda function
      const response = await fetch('https://qeod2f5oh4tcuewrgv3ay6vmba0uwnmh.lambda-url.us-west-2.on.aws/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitMessage('✅ ' + result.message);
        setFormData({ name: '', phone: '', email: '' });
        setTimeout(() => {
          setIsModalOpen(false);
          setSubmitMessage('');
        }, 3000);
      } else {
        throw new Error(result.message || 'Failed to send demo request');
      }
      
    } catch (error) {
      console.error('Error sending demo request:', error);
      // Fallback to mailto
      const subject = encodeURIComponent('Demo Request - BlueCollarTech.ai');
      const body = encodeURIComponent(
        `Hello BlueCollarTech.ai Team,\n\n` +
        `I would like to schedule a demo of your AI phone answering service.\n\n` +
        `Contact Information:\n` +
        `• Name: ${formData.name}\n` +
        `• Phone: ${formData.phone}\n` +
        `• Email: ${formData.email}\n\n` +
        `Please contact me to schedule a convenient time for the demo.\n\n` +
        `Thank you!`
      );
      
      window.location.href = `mailto:info@bluecollartech.ai?subject=${subject}&body=${body}`;
      setSubmitMessage('⚠️ Server unavailable. Opening email client as backup.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openDemoModal = () => {
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 opacity-20">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-blue-600 animate-pulse"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.5) 0%, transparent 50%),
                           radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.5) 0%, transparent 50%),
                           radial-gradient(circle at 40% 20%, rgba(34, 211, 238, 0.5) 0%, transparent 50%)`
        }}></div>
      </div>

      {/* Navigation */}
      <nav className="relative z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                BlueCollarTech.ai
              </span>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="hover:text-blue-400 transition-colors">Features</a>
              <a href="#industries" className="hover:text-blue-400 transition-colors">Industries</a>
              <a href="#pricing" className="hover:text-blue-400 transition-colors">Pricing</a>
              <a href="#demo" className="hover:text-blue-400 transition-colors">Demo</a>
              <button className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full hover:shadow-lg hover:shadow-purple-500/25 transition-all">
                Start Free Trial
              </button>
            </div>

            <button 
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 pt-20 pb-32 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center">
            {/* AI Badge */}
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6 border border-white/20">
              <Sparkles className="w-4 h-4 text-yellow-400" />
              <span className="text-sm">Powered by Advanced AI Technology</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                AI That Never Sleeps
              </span>
              <br />
              <span className="text-white">So You Can</span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Revolutionary AI voice technology that answers calls, books appointments, and grows your business 24/7. 
              Built for every trade, from HVAC to construction.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button className="group px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full text-lg font-semibold hover:shadow-2xl hover:shadow-purple-500/25 transition-all transform hover:scale-105">
                Start 14-Day Free Trial
                <ArrowRight className="inline ml-2 group-hover:translate-x-1 transition-transform" />
              </button>
              <button 
                onClick={openDemoModal}
                className="px-8 py-4 bg-white/10 backdrop-blur-sm rounded-full text-lg font-semibold border border-white/20 hover:bg-white/20 transition-all flex items-center justify-center"
              >
                <Play className="mr-2" />
                Schedule Demo Call
              </button>
            </div>

            {/* Live Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <Activity className="w-5 h-5 text-green-400 animate-pulse" />
                  <span className="text-3xl font-bold text-green-400">2,847</span>
                </div>
                <p className="text-sm text-gray-400">Calls Handled Today</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <Users className="w-5 h-5 text-blue-400" />
                  <span className="text-3xl font-bold text-blue-400">500+</span>
                </div>
                <p className="text-sm text-gray-400">Active Businesses</p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-purple-400" />
                  <span className="text-3xl font-bold text-purple-400">$2.4M</span>
                </div>
                <p className="text-sm text-gray-400">Revenue Recovered</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Technology Showcase */}
      <section id="features" className="relative z-10 py-20 px-6 bg-gradient-to-b from-transparent via-purple-900/20 to-transparent">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                Next-Generation AI Technology
              </span>
            </h2>
            <p className="text-xl text-gray-300">Powered by the same AI that powers Fortune 500 companies</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="group bg-gradient-to-br from-blue-900/50 to-purple-900/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-blue-400/50 transition-all hover:transform hover:scale-105">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center mb-4">
                <Brain className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Neural Processing</h3>
              <p className="text-sm text-gray-400">Advanced NLP understands context, accent, and industry jargon</p>
            </div>

            <div className="group bg-gradient-to-br from-purple-900/50 to-pink-900/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-purple-400/50 transition-all hover:transform hover:scale-105">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                <Cpu className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Real-Time Learning</h3>
              <p className="text-sm text-gray-400">Adapts to your business patterns and customer preferences</p>
            </div>

            <div className="group bg-gradient-to-br from-cyan-900/50 to-blue-900/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-cyan-400/50 transition-all hover:transform hover:scale-105">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-xl flex items-center justify-center mb-4">
                <Zap className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Lightning Fast</h3>
              <p className="text-sm text-gray-400">Sub-second response time with parallel processing</p>
            </div>

            <div className="group bg-gradient-to-br from-green-900/50 to-cyan-900/50 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-green-400/50 transition-all hover:transform hover:scale-105">
              <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Enterprise Security</h3>
              <p className="text-sm text-gray-400">Bank-level encryption and HIPAA-compliant infrastructure</p>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industries" className="relative z-10 py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                AI for Every Trade
              </span>
            </h2>
            <p className="text-xl text-gray-300">Specialized intelligence for your specific industry</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {industries.map((industry) => (
              <button
                key={industry.id}
                onClick={() => setActiveIndustry(industry.id)}
                className={`p-4 rounded-2xl border transition-all ${
                  activeIndustry === industry.id 
                    ? 'bg-gradient-to-br from-blue-600 to-purple-600 border-transparent shadow-lg shadow-purple-500/25' 
                    : 'bg-white/5 border-white/10 hover:bg-white/10'
                }`}
              >
                <div className="text-3xl mb-2">{industry.icon}</div>
                <div className="text-sm font-medium">{industry.name}</div>
              </button>
            ))}
          </div>

          {/* Industry-Specific Features */}
          <div className="bg-gradient-to-br from-blue-900/30 to-purple-900/30 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-bold mb-6">Industry-Specific Intelligence</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-400 mt-1" />
                    <div>
                      <p className="font-medium">Emergency Detection</p>
                      <p className="text-sm text-gray-400">Recognizes "no heat", "pipe burst", "power out" and more</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-400 mt-1" />
                    <div>
                      <p className="font-medium">Smart Scheduling</p>
                      <p className="text-sm text-gray-400">Knows service areas, travel time, and job durations</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-400 mt-1" />
                    <div>
                      <p className="font-medium">Price Intelligence</p>
                      <p className="text-sm text-gray-400">Provides estimates based on your pricing rules</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-400 mt-1" />
                    <div>
                      <p className="font-medium">Compliance Aware</p>
                      <p className="text-sm text-gray-400">Follows industry regulations and licensing requirements</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-black/30 rounded-2xl p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Mic className="w-5 h-5 text-red-400 animate-pulse" />
                  <span className="text-sm font-medium">Live Call Example</span>
                </div>
                <div className="space-y-3">
                  <div className="bg-blue-600/20 rounded-lg p-3 border-l-2 border-blue-400">
                    <p className="text-sm"><span className="text-blue-400">Customer:</span> "My AC stopped working and it's 95 degrees!"</p>
                  </div>
                  <div className="bg-purple-600/20 rounded-lg p-3 border-l-2 border-purple-400">
                    <p className="text-sm"><span className="text-purple-400">AI:</span> "I understand this is an emergency. I can get a technician to you within 2 hours. Let me check availability..."</p>
                  </div>
                  <div className="bg-green-600/20 rounded-lg p-3 border-l-2 border-green-400">
                    <p className="text-xs text-green-400">✓ Emergency detected • ✓ Prioritized scheduling • ✓ SMS sent to on-call tech</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Calculator Section */}
      <section className="relative z-10 py-20 px-6 bg-gradient-to-b from-transparent via-blue-900/20 to-transparent">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent">
                Calculate Your ROI
              </span>
            </h2>
            <p className="text-xl text-gray-300">See how much revenue you're losing to missed calls</p>
          </div>

          <div className="bg-gradient-to-br from-green-900/30 to-blue-900/30 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Missed Calls Per Week</label>
                  <input
                    type="range"
                    min="5"
                    max="50"
                    value={missedCalls}
                    onChange={(e) => setMissedCalls(Number(e.target.value))}
                    className="w-full accent-blue-500"
                  />
                  <div className="flex justify-between text-sm text-gray-400 mt-1">
                    <span>5</span>
                    <span className="text-blue-400 font-bold">{missedCalls} calls</span>
                    <span>50</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Average Job Value</label>
                  <select 
                    value={jobValue}
                    onChange={(e) => setJobValue(Number(e.target.value))}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white"
                  >
                    <option value="500">$200 - $500</option>
                    <option value="1000">$500 - $1,000</option>
                    <option value="2000">$1,000 - $2,500</option>
                    <option value="5000">$2,500+</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Select Plan</label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => setSelectedPlan('starter')}
                      className={`py-2 rounded-lg text-sm ${selectedPlan === 'starter' ? 'bg-blue-600' : 'bg-white/10'}`}
                    >
                      Starter
                    </button>
                    <button
                      onClick={() => setSelectedPlan('professional')}
                      className={`py-2 rounded-lg text-sm ${selectedPlan === 'professional' ? 'bg-blue-600' : 'bg-white/10'}`}
                    >
                      Professional
                    </button>
                    <button
                      onClick={() => setSelectedPlan('enterprise')}
                      className={`py-2 rounded-lg text-sm ${selectedPlan === 'enterprise' ? 'bg-blue-600' : 'bg-white/10'}`}
                    >
                      Enterprise
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-500/20 to-blue-500/20 rounded-2xl p-6 border border-green-400/30">
                <h3 className="text-2xl font-bold mb-4">Your Monthly ROI</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Lost Revenue:</span>
                    <span className="text-2xl font-bold text-red-400">
                      ${calculateROI().revenue.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">AI Investment:</span>
                    <span className="text-xl">
                      ${selectedPlan === 'starter' ? 99 : selectedPlan === 'professional' ? 299 : 499}
                    </span>
                  </div>
                  <div className="border-t border-white/20 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Return on Investment:</span>
                      <span className="text-3xl font-bold text-green-400">
                        {calculateROI().roi}%
                      </span>
                    </div>
                  </div>
                </div>
                <button 
                  onClick={openDemoModal}
                  className="w-full mt-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 rounded-xl font-semibold hover:shadow-lg hover:shadow-green-500/25 transition-all"
                >
                  Schedule Demo Call →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="relative z-10 py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Simple, Transparent Pricing
              </span>
            </h2>
            <p className="text-xl text-gray-300">No setup fees. No contracts. Cancel anytime.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Starter Plan */}
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:border-blue-400/50 transition-all">
              <div className="mb-6">
                <h3 className="text-2xl font-bold mb-2">Starter</h3>
                <p className="text-gray-400">For solo operators</p>
              </div>
              <div className="mb-6">
                <span className="text-5xl font-bold">$99</span>
                <span className="text-gray-400">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <span className="text-sm">300 minutes (5 hours)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <span className="text-sm">24/7 AI answering</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <span className="text-sm">Emergency detection</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <span className="text-sm">SMS confirmations</span>
                </li>
                <li className="flex items-start space-x-2">
                  <X className="w-5 h-5 text-red-400 mt-0.5" />
                  <span className="text-sm text-gray-500">Website not included</span>
                </li>
              </ul>
              <button className="w-full py-3 bg-white/10 rounded-xl font-semibold hover:bg-white/20 transition-all">
                Start Free Trial
              </button>
            </div>

            {/* Professional Plan */}
            <div className="relative bg-gradient-to-br from-blue-900/50 to-purple-900/50 backdrop-blur-sm rounded-3xl p-8 border-2 border-blue-400/50 transform scale-105">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="px-4 py-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full text-sm font-semibold">
                  MOST POPULAR
                </span>
              </div>
              <div className="mb-6 mt-2">
                <h3 className="text-2xl font-bold mb-2">Professional</h3>
                <p className="text-gray-400">For growing teams</p>
              </div>
              <div className="mb-6">
                <span className="text-5xl font-bold">$299</span>
                <span className="text-gray-400">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <span className="text-sm">800 minutes (13+ hours)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <span className="text-sm">Everything in Starter</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <span className="text-sm font-semibold text-blue-400">✨ Professional website included</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <span className="text-sm font-semibold text-blue-400">✨ Local SEO optimization</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <span className="text-sm">Advanced scheduling</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <span className="text-sm">Photo upload system</span>
                </li>
              </ul>
              <button className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/25 transition-all">
                Start Free Trial
              </button>
              <p className="text-xs text-center mt-3 text-green-400">Save $1,200/year vs competitors</p>
            </div>

            {/* Enterprise Plan */}
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10 hover:border-purple-400/50 transition-all">
              <div className="mb-6">
                <h3 className="text-2xl font-bold mb-2">Enterprise</h3>
                <p className="text-gray-400">For multiple locations</p>
              </div>
              <div className="mb-6">
                <span className="text-5xl font-bold">$499</span>
                <span className="text-gray-400">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <span className="text-sm">1,200 minutes (20+ hours)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <span className="text-sm">Everything in Professional</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <span className="text-sm">Unlimited location pages</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <span className="text-sm">Google Ads tracking</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <span className="text-sm">Custom AI personality</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <span className="text-sm">White-label option</span>
                </li>
              </ul>
              <button className="w-full py-3 bg-white/10 rounded-xl font-semibold hover:bg-white/20 transition-all">
                Contact Sales
              </button>
            </div>
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-400">All plans include: $0.15/minute overage • No setup fees • Cancel anytime</p>
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section id="demo" className="relative z-10 py-20 px-6 bg-gradient-to-b from-transparent via-purple-900/20 to-transparent">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Experience the Future
              </span>
            </h2>
            <p className="text-xl text-gray-300">Call our AI right now and see the magic happen</p>
          </div>

          <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full mb-6 animate-pulse">
                <Phone className="w-10 h-10" />
              </div>
              <h3 className="text-3xl font-bold mb-2">1-800-AI-WORKS</h3>
              <p className="text-gray-400 mb-8">Call now to experience your future AI assistant</p>
              
              <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                <button className="p-4 bg-white/10 rounded-xl hover:bg-white/20 transition-all text-left">
                  <div className="font-semibold mb-1">Test Emergency Call</div>
                  <div className="text-sm text-gray-400">See how AI handles urgent situations</div>
                </button>
                <button className="p-4 bg-white/10 rounded-xl hover:bg-white/20 transition-all text-left">
                  <div className="font-semibold mb-1">Schedule Appointment</div>
                  <div className="text-sm text-gray-400">Book a service call with the AI</div>
                </button>
                <button className="p-4 bg-white/10 rounded-xl hover:bg-white/20 transition-all text-left">
                  <div className="font-semibold mb-1">Get Price Quote</div>
                  <div className="text-sm text-gray-400">Ask about service pricing</div>
                </button>
                <button className="p-4 bg-white/10 rounded-xl hover:bg-white/20 transition-all text-left">
                  <div className="font-semibold mb-1">Business Hours</div>
                  <div className="text-sm text-gray-400">Check availability and hours</div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="relative z-10 py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Trusted by the Trades
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <div className="flex items-center space-x-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-gray-300 mb-4">"Went from missing 30% of calls to zero. Revenue up 40% in 3 months. This AI is better than any receptionist we've had."</p>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full"></div>
                <div>
                  <p className="font-semibold">Mike Johnson</p>
                  <p className="text-sm text-gray-400">Johnson HVAC, Dallas TX</p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <div className="flex items-center space-x-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-gray-300 mb-4">"The website alone is worth $500/month. Getting the AI answering service too? It's a no-brainer. Best investment we've made."</p>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full"></div>
                <div>
                  <p className="font-semibold">Sarah Chen</p>
                  <p className="text-sm text-gray-400">Chen Plumbing, Seattle WA</p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <div className="flex items-center space-x-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-gray-300 mb-4">"5 locations, one AI system. Saves us $15k/month vs hiring receptionists. Setup took 30 minutes. Incredible."</p>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-full"></div>
                <div>
                  <p className="font-semibold">Tom Rodriguez</p>
                  <p className="text-sm text-gray-400">AllTrade Services, Phoenix AZ</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-20 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-3xl p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-black/20"></div>
            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                Ready to Stop Losing Money?
              </h2>
              <p className="text-xl mb-8 text-gray-200">
                Join 500+ contractors using AI to grow their business. 
                14-day free trial. No credit card required.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="px-8 py-4 bg-white text-gray-900 rounded-full text-lg font-semibold hover:shadow-2xl hover:shadow-white/25 transition-all transform hover:scale-105">
                  Start Free Trial Now
                </button>
                <button 
                  onClick={openDemoModal}
                  className="px-8 py-4 bg-white/20 backdrop-blur-sm rounded-full text-lg font-semibold border border-white/30 hover:bg-white/30 transition-all"
                >
                  Schedule Demo Call
                </button>
              </div>
              <p className="mt-6 text-sm text-gray-300">
                ⚡ Get 50% off your first 3 months • Limited time offer
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 py-12 px-6 border-t border-white/10">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold">BlueCollarTech.ai</span>
              </div>
              <p className="text-sm text-gray-400">AI-powered growth for every trade professional.</p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Industries</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Status</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-white/10 pt-8 text-center text-sm text-gray-400">
            <p>© 2025 BlueCollarTech.ai • Built in the USA 🇺🇸 • Powered by AI</p>
          </div>
        </div>
      </footer>

      {/* Demo Request Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-gradient-to-br from-gray-900 to-black rounded-3xl p-8 max-w-md w-full border border-white/20">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Schedule Demo Call
              </h3>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <p className="text-gray-400 mb-6">
              Get a personalized demo and see how AI can transform your business.
            </p>
            
            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none transition-colors"
                  placeholder="Enter your full name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none transition-colors"
                  placeholder="(555) 123-4567"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none transition-colors"
                  placeholder="your@email.com"
                />
              </div>
              
              {submitMessage && (
                <div className="p-4 bg-green-500/20 border border-green-500/30 rounded-xl">
                  <p className="text-sm text-green-300">{submitMessage}</p>
                </div>
              )}
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Request Demo Call'}
              </button>
            </form>
            
            <p className="text-xs text-gray-500 mt-4 text-center">
              We'll contact you within 24 hours to schedule your personalized demo.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlueCollarTechWebsite;
